<script setup>
import { useRouter } from "vue-router";

const router = useRouter();

const NAV = [
  {
    id: "users",
    label: "Users & Moderation",
    icon: "pi-users",
    colorClass: "section--indigo",
    desc: "Accounts, stats and device firmware",
    items: [
      { label: "Users", icon: "pi-user", desc: "Browse and manage user accounts", to: "/user" },
      {
        label: "Metrics",
        icon: "pi-chart-bar",
        desc: "Platform statistics and activity insights",
        to: "/metrics",
      },
      {
        label: "Firmware",
        icon: "pi-server",
        desc: "Device firmware versions and updates",
        to: "/firmware",
        colorClass: "tile--amber",
      },
    ],
  },
  {
    id: "content",
    label: "Content",
    icon: "pi-th-large",
    colorClass: "section--purple",
    desc: "Practice, exercises and news",
    items: [
      {
        label: "Shared Practice",
        icon: "pi-share-alt",
        desc: "User-shared practice sessions",
        to: "/shared-practice",
      },
      {
        label: "News Feed",
        icon: "pi-megaphone",
        desc: "Platform news and announcements",
        to: "/news-feed",
      },
      {
        label: "SG Library",
        icon: "pi-bookmark",
        desc: "Built-in exercise library",
        to: "/sg-library",
      },
    ],
  },
  {
    id: "comms",
    label: "Communication",
    icon: "pi-send",
    colorClass: "section--blue",
    desc: "Emails and delivery logs",
    items: [
      {
        label: "Mass Mailing",
        icon: "pi-envelope",
        desc: "Send bulk email campaigns",
        to: "/mass-mailing",
      },
      {
        label: "Mailing Logs",
        icon: "pi-list",
        desc: "Delivery history and details",
        to: "/mailing-logs",
      },
    ],
  },
];

function navigate(to) {
  router.push(to);
}
</script>

<template>
  <div class="nav-hub">
    <div class="nav-hub__inner">
      <div class="nav-hub__header">
        <h1 class="nav-hub__title">Admin Panel</h1>
        <p class="nav-hub__subtitle">Select a section to continue.</p>
      </div>

      <div class="nav-hub__sections">
        <div
          v-for="section in NAV"
          :key="section.id"
          class="nav-section"
          :class="section.colorClass"
        >
          <div class="nav-section__head">
            <div class="nav-section__icon">
              <i :class="`pi ${section.icon}`" class="nav-section__glyph" />
            </div>
            <span class="nav-section__label">{{ section.label }}</span>
            <span class="nav-section__desc">{{ section.desc }}</span>
          </div>

          <div class="nav-section__tiles">
            <div
              v-for="item in section.items"
              :key="item.label"
              class="nav-tile"
              :class="item.colorClass"
              @click="navigate(item.to)"
            >
              <div class="nav-tile__icon">
                <i :class="`pi ${item.icon}`" class="nav-tile__glyph" />
              </div>
              <div class="nav-tile__label">{{ item.label }}</div>
              <div class="nav-tile__desc">{{ item.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// ── colour tokens ─────────────────────────────────────────────────────────────
.section--indigo {
  --cat-rgb: 99, 102, 241;
  --tile-rgb: 99, 102, 241;
}
.section--purple {
  --cat-rgb: 168, 85, 247;
  --tile-rgb: 168, 85, 247;
}
.section--blue {
  --cat-rgb: 59, 130, 246;
  --tile-rgb: 59, 130, 246;
}

.tile--amber {
  --tile-rgb: 245, 158, 11;
}

// ── nav-hub ───────────────────────────────────────────────────────────────────
.nav-hub {
  min-height: 100%;
  background: var(--surface-ground);
}

.nav-hub__inner {
  max-width: 972px;
  margin: 0 auto;
  padding: 1.5dvh 2rem 5rem;
}

.nav-hub__header {
  margin-bottom: 3rem;
}

.nav-hub__title {
  font-size: 1.67rem;
  font-weight: 800;
  color: var(--text-color);
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin: 0;
}

.nav-hub__subtitle {
  font-size: 0.92rem;
  color: var(--text-color-secondary);
  margin: 0.38rem 0 0;
}

.nav-hub__sections {
  display: flex;
  flex-direction: column;
  gap: 2.7rem;
}

// ── nav-section ───────────────────────────────────────────────────────────────
.nav-section__head {
  display: flex;
  align-items: center;
  gap: 0.59rem;
  margin-bottom: 0.92rem;
}

.nav-section__icon {
  width: 1.89rem;
  height: 1.89rem;
  border-radius: 0.49rem;
  background: rgba(var(--cat-rgb), 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-section__glyph {
  color: rgb(var(--cat-rgb));
  font-size: 0.84rem;
}

.nav-section__label {
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--text-color);
  letter-spacing: -0.01em;
}

.nav-section__desc {
  font-size: 0.78rem;
  color: var(--text-color-secondary);
}

.nav-section__tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 0.81rem;
}

// ── nav-tile ──────────────────────────────────────────────────────────────────
.nav-tile {
  flex: 1 1 194px;
  min-width: 173px;
  max-width: 281px;
  padding: 1.35rem 1.08rem;
  border-radius: 0.945rem;
  background: var(--surface-card);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.65rem;

  &:hover {
    background: rgba(var(--tile-rgb), 0.06);
    border-color: rgba(var(--tile-rgb), 0.18);
    box-shadow: 0 6px 24px rgba(var(--tile-rgb), 0.1);

    .nav-tile__icon {
      background: rgba(var(--tile-rgb), 0.14);
    }

    .nav-tile__label {
      color: rgb(var(--tile-rgb));
    }
  }
}

.nav-tile__icon {
  width: 2.81rem;
  height: 2.81rem;
  border-radius: 0.81rem;
  background: rgba(var(--tile-rgb), 0.09);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s ease;
  flex-shrink: 0;
}

.nav-tile__glyph {
  color: rgb(var(--tile-rgb));
  font-size: 1.19rem;
}

.nav-tile__label {
  font-size: 0.885rem;
  font-weight: 600;
  color: var(--text-color);
  line-height: 1.25;
  transition: color 0.18s ease;
}

.nav-tile__desc {
  font-size: 0.735rem;
  color: var(--text-color-secondary);
  line-height: 1.4;
}
</style>
